import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import dataService from '../../services/dataService';
import { BASE_URL } from '../../services/backendData';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/owner-dashboard — merchant "headquarters" in brand primitives.
 *
 * Shows only real data. The restaurant picker lists the signed-in owner's
 * restaurants (GET /listRestaurants?user_id=…, which the backend scopes to
 * the owner). The only per-restaurant numbers we have today are the
 * public rating and review count; everything else (covers, revenue,
 * allergen logs, guests, staff, specials) has no backend yet, so each
 * section renders an honest "not connected yet" state instead of demo data.
 *
 * Tabs: Overview | Menu | Specials | Allergens | Guests | Reviews | Staff
 */

const EASE = [0.22, 1, 0.36, 1];

const TABS = [
  { id: 'overview',  label: 'Overview',  icon: 'chart',    tone: 'blue'   },
  { id: 'menu',      label: 'Menu',      icon: 'menu',     tone: 'blue'   },
  { id: 'specials',  label: 'Specials',  icon: 'flame',    tone: 'orange' },
  { id: 'allergens', label: 'Allergens', icon: 'shield',   tone: 'red'    },
  { id: 'guests',    label: 'Guests',    icon: 'people',   tone: 'purple' },
  { id: 'reviews',   label: 'Reviews',   icon: 'star',     tone: 'orange' },
  { id: 'staff',     label: 'Staff',     icon: 'badge',    tone: 'green'  },
];

const TONE_COLORS = {
  blue:   { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  solid: 'var(--cwm-blue-deep)' },
  green:  { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: 'var(--cwm-green)' },
  orange: { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', solid: 'var(--cwm-amber)' },
  red:    { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   solid: '#B91C1C' },
  purple: { bg: '#F1E8FE',                fg: '#5B21B6',               solid: '#7C3AED' },
};

// The backend's listRestaurants accepts user_id and restricts the result to
// restaurants that user owns (admins see all). dataService.listRestaurants
// doesn't expose that parameter, so call the same endpoint directly here.
async function fetchOwnedRestaurants(userId) {
  const res = await fetch(`${BASE_URL}/listRestaurants?per_page=500&user_id=${encodeURIComponent(userId)}`);
  if (!res.ok) throw new Error(`listRestaurants failed (${res.status})`);
  const data = await res.json();
  return Array.isArray(data) ? data : (data?.restaurants || []);
}

export default function UnifiedOwnerDashboard() {
  const navigate = useNavigate();
  const { userId, isInitialized } = useContext(UserContext);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Analytics for the selected restaurant. The endpoint isn't live yet
  // (dataService.getOwnerAnalytics returns null), so every tab renders an
  // empty state unless real fields arrive.
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) { navigate('/unified'); return; }
    let cancelled = false;
    (async () => {
      try {
        const arr = await fetchOwnedRestaurants(userId);
        if (cancelled) return;
        setRestaurants(arr);
        setLoadError(false);
        if (arr.length) setSelectedId(String(arr[0].id));
      } catch {
        if (!cancelled) { setRestaurants([]); setLoadError(true); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, isInitialized, navigate]);

  useEffect(() => {
    if (!selectedId) { setAnalytics(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await dataService.getOwnerAnalytics(selectedId);
        if (!cancelled) setAnalytics(data || null);
      } catch {
        if (!cancelled) setAnalytics(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  const selected = useMemo(() => restaurants.find((r) => String(r.id) === String(selectedId)), [restaurants, selectedId]);
  const noRestaurants = !loading && restaurants.length === 0;

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '28px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: EASE }}
          style={{
            position: 'relative', overflow: 'hidden',
            padding: '22px 24px',
            borderRadius: 26,
            background: 'linear-gradient(160deg, #0A2540 0%, #0F5FB8 60%, #1E9BFF 100%)',
            color: '#fff',
            marginBottom: 18,
            boxShadow: '0 20px 44px rgba(10,37,64,0.3)',
          }}
        >
          <div aria-hidden style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(circle, rgba(255,255,255,0.18) 0, rgba(255,255,255,0.18) 1.5px, transparent 1.6px)',
            backgroundSize: '22px 22px', opacity: 0.36,
          }}/>
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.82, marginBottom: 4 }}>
                OWNER DASHBOARD
              </div>
              <h1 style={{ margin: 0, fontSize: 32, fontWeight: 900, letterSpacing: '-0.025em', lineHeight: 1.05 }}>
                {selected?.name || (loading ? 'Loading…' : 'No restaurant linked yet')}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 700, opacity: 0.88 }}>
                  {selected?.cuisine_type ? `${selected.cuisine_type} · ` : ''}{todayFormatted()}
                </span>
              </div>
            </div>
            {restaurants.length > 1 && (
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                aria-label="Choose restaurant"
                style={{
                  padding: '8px 32px 8px 14px',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,0.18)',
                  color: '#fff',
                  border: '1.5px solid rgba(255,255,255,0.3)',
                  fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800,
                  backdropFilter: 'blur(6px)',
                  cursor: 'pointer',
                  appearance: 'none',
                  outline: 'none',
                }}>
                {restaurants.map((r) => (
                  <option key={r.id} value={String(r.id)} style={{ color: 'var(--cwm-ink)' }}>{r.name}</option>
                ))}
              </select>
            )}
          </div>
        </motion.section>

        {noRestaurants ? (
          <EmptyCard
            tone="blue"
            eyebrow={loadError ? "COULDN'T LOAD" : 'NO RESTAURANT YET'}
            title={loadError ? "We couldn't load your restaurants" : 'No restaurant is linked to your account'}
            body={loadError
              ? 'Check your connection and refresh the page.'
              : 'Once a restaurant is added under your account, its dashboard shows up here.'}
          />
        ) : (
          <>
            {/* Tab strip */}
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.08, ease: EASE }}
              style={{
                display: 'flex', gap: 4, padding: 4,
                background: 'rgba(255,255,255,0.88)',
                border: '1px solid rgba(255,255,255,0.95)',
                borderRadius: 999,
                boxShadow: '0 8px 20px rgba(10,37,64,0.08)',
                marginBottom: 18,
                overflowX: 'auto',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
              }}
            >
              {TABS.map((t) => {
                const on = activeTab === t.id;
                const tone = TONE_COLORS[t.tone];
                return (
                  <button key={t.id} type="button" onClick={() => setActiveTab(t.id)}
                          style={{
                            flex: '0 0 auto',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            padding: '10px 14px', borderRadius: 999,
                            background: on ? `linear-gradient(90deg, ${tone.solid}, ${tone.solid})` : 'transparent',
                            color: on ? '#fff' : 'var(--cwm-gray-700)',
                            border: 'none', cursor: 'pointer',
                            fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 900, whiteSpace: 'nowrap',
                            boxShadow: on ? `0 6px 14px ${tone.solid}44` : 'none',
                            transition: 'all 180ms ease',
                          }}>
                    <TabIcon name={t.icon} size={14} /> {t.label}
                  </button>
                );
              })}
            </motion.div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.28, ease: EASE }}
              >
                {activeTab === 'overview'  && <OverviewTab restaurant={selected} analytics={analytics} />}
                {activeTab === 'menu'      && <MenuTab onOpen={() => navigate('/menu-manager')} />}
                {activeTab === 'specials'  && <SpecialsTab onOpen={() => navigate('/menu-manager')} />}
                {activeTab === 'allergens' && <AllergensTab analytics={analytics} />}
                {activeTab === 'guests'    && <GuestsTab />}
                {activeTab === 'reviews'   && <ReviewsTab restaurant={selected} analytics={analytics} />}
                {activeTab === 'staff'     && <StaffTab onTrain={() => navigate('/server-dashboard')} />}
              </motion.div>
            </AnimatePresence>
          </>
        )}
      </div>
    </AppShell>
  );
}

// ============================================================
// SHARED
// ============================================================

const NOT_CONNECTED = 'No data yet — connects when POS & analytics launch.';

function hasNumber(v) { return typeof v === 'number' && Number.isFinite(v); }

function ratingValue(restaurant) {
  const r = Number(restaurant?.display_rating);
  return Number.isFinite(r) && r > 0 ? r : null;
}

function reviewCount(restaurant) {
  const n = Number(restaurant?.display_review_count);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function EmptyCard({ eyebrow, title, body, tone = 'blue', action }) {
  const t = TONE_COLORS[tone] || TONE_COLORS.blue;
  return (
    <div className="cwm-sheet" style={{ padding: 22 }}>
      {eyebrow && <div className="cwm-eyebrow" style={{ color: t.fg }}>{eyebrow}</div>}
      {title && (
        <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2, marginBottom: 8 }}>
          {title}
        </div>
      )}
      <div style={{
        padding: '18px 16px', borderRadius: 14,
        background: 'var(--cwm-gray-50)', border: '1.5px dashed var(--cwm-gray-200)',
        textAlign: 'center', fontSize: 13, fontWeight: 700, color: 'var(--cwm-gray-500)', lineHeight: 1.5,
      }}>
        {body || NOT_CONNECTED}
      </div>
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );
}

function KpiTile({ label, value, hint, tone }) {
  const t = TONE_COLORS[tone] || TONE_COLORS.blue;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.36, ease: EASE }}
      className="cwm-sheet"
      style={{ padding: 16 }}
    >
      <div className="cwm-eyebrow" style={{ color: t.fg }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 28, fontWeight: 900, color: value === '—' ? 'var(--cwm-gray-400)' : 'var(--cwm-ink)', letterSpacing: '-0.02em', lineHeight: 1, marginTop: 4 }}>
        {value}
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 3 }}>{hint}</div>
    </motion.div>
  );
}

function todayFormatted() {
  return new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

// ============================================================
// OVERVIEW TAB
// ============================================================

function OverviewTab({ restaurant, analytics }) {
  const rating = ratingValue(restaurant);
  const count = reviewCount(restaurant);
  const menuChats = analytics?.kpis?.menu_chats;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
        <KpiTile label="Rating" value={rating ? `${rating.toFixed(1)}★` : '—'} hint={rating ? 'from diner reviews' : 'no ratings yet'} tone="orange" />
        <KpiTile label="Reviews" value={count !== null ? String(count) : '—'} hint="on ChatWithMenu" tone="blue" />
        <KpiTile label="Menu chats" value={hasNumber(menuChats) ? String(menuChats) : '—'} hint={hasNumber(menuChats) ? 'diner conversations' : 'analytics coming soon'} tone="purple" />
        <KpiTile label="Covers & revenue" value="—" hint="POS integration coming soon" tone="green" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
        <EmptyCard tone="blue" eyebrow="COVERS PER HOUR" title="Service pace" />
        <EmptyCard tone="green" eyebrow="TOP DISHES" title="By revenue" />
        <AllergenQuestionsCard analytics={analytics} />
        <EmptyCard tone="purple" eyebrow="STAFF ACTIVITY" title="Recent shifts" />
      </div>
    </div>
  );
}

function AllergenQuestionsCard({ analytics }) {
  const live = analytics?.allergen_questions;
  if (!Array.isArray(live) || live.length === 0) {
    return <EmptyCard tone="red" eyebrow="ALLERGEN QUESTIONS" title="What diners ask about" />;
  }
  const maxCount = Math.max(...live.map((r) => r.count || 0), 1);
  return (
    <div className="cwm-sheet" style={{ padding: 20 }}>
      <div className="cwm-eyebrow" style={{ color: 'var(--cwm-red-text)' }}>ALLERGEN QUESTIONS</div>
      <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2 }}>What diners ask about</div>
      <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
        {live.map((a, i) => {
          const tone = /(nut|peanut|shellfish)/i.test(a.allergen) ? TONE_COLORS.red : TONE_COLORS.orange;
          const pct = ((a.count || 0) / maxCount) * 100;
          return (
            <div key={a.allergen + i} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 40px', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 900, color: tone.fg, textTransform: 'capitalize' }}>{a.allergen}</span>
              <div style={{ height: 8, borderRadius: 999, background: 'var(--cwm-gray-100)', overflow: 'hidden' }}>
                <motion.div
                  initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.6, delay: 0.15 + i * 0.05, ease: EASE }}
                  style={{ height: '100%', background: tone.solid, borderRadius: 999 }}/>
              </div>
              <span style={{ fontSize: 13, fontWeight: 900, color: 'var(--cwm-ink)', textAlign: 'right' }}>{a.count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// MENU TAB
// ============================================================

function MenuTab({ onOpen }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
      <EmptyCard
        tone="blue"
        eyebrow="MENU HEALTH"
        title="Quality check"
        body="Menu completeness scores are coming soon."
        action={(
          <button onClick={onOpen} className="cwm-btn" style={{ width: '100%', justifyContent: 'center' }}>
            <PencilIcon size={14} /> Open Menu Manager
          </button>
        )}
      />

      <div className="cwm-sheet" style={{ padding: 22 }}>
        <div className="cwm-eyebrow" style={{ color: 'var(--cwm-amber-text)' }}>QUICK ACTIONS</div>
        <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2, marginBottom: 18 }}>
          Common menu moves
        </div>
        <div style={{ display: 'grid', gap: 8 }}>
          <QuickLink icon="menu"   label="Edit menu items"         sub="Add items, change prices, tag allergens" onClick={onOpen} />
          <QuickLink icon="store"  label="Edit restaurant profile" sub="Hours, address, amenities, phone"        onClick={onOpen} />
        </div>
      </div>
    </div>
  );
}

function QuickLink({ icon, label, sub, onClick }) {
  return (
    <motion.button type="button" onClick={onClick}
      whileHover={{ x: 3 }} whileTap={{ scale: 0.98 }}
      style={{
        padding: '12px 14px', borderRadius: 14,
        background: '#fff', color: 'var(--cwm-ink)',
        border: '1.5px solid var(--cwm-gray-200)',
        fontFamily: 'var(--cwm-font)', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 12,
        textAlign: 'left',
      }}>
      <span style={{
        width: 36, height: 36, borderRadius: 10,
        background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        <TabIcon name={icon} size={16} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 900, color: 'var(--cwm-ink)' }}>{label}</span>
        <span style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>{sub}</span>
      </span>
      <ArrowIcon size={14} color="var(--cwm-gray-400)" />
    </motion.button>
  );
}

// ============================================================
// SPECIALS TAB
// ============================================================

function SpecialsTab({ onOpen }) {
  return (
    <EmptyCard
      tone="orange"
      eyebrow="SPECIALS"
      title="No specials yet"
      body="Publishing specials to diners is coming soon."
      action={(
        <button className="cwm-btn" onClick={onOpen}>
          <PencilIcon size={12} /> Open Menu Manager
        </button>
      )}
    />
  );
}

// ============================================================
// ALLERGENS TAB
// ============================================================

function AllergensTab({ analytics }) {
  const live = analytics?.allergen_questions;
  const liveTotal = Array.isArray(live) ? live.reduce((n, q) => n + (q.count || 0), 0) : null;
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <KpiTile label="Allergen questions" value={hasNumber(liveTotal) ? String(liveTotal) : '—'} hint={hasNumber(liveTotal) ? 'from diner Q&A' : 'analytics coming soon'} tone="blue" />
      </div>
      <AllergenQuestionsCard analytics={analytics} />
      <EmptyCard tone="red" eyebrow="ALLERGEN LOG" title="Guest questions and how they were resolved" body="No allergen log yet — service-floor tracking is coming soon." />
    </div>
  );
}

// ============================================================
// GUESTS TAB
// ============================================================

function GuestsTab() {
  return (
    <EmptyCard
      tone="purple"
      eyebrow="GUESTS"
      title="Returning guests and safety profiles"
      body="No guest data yet — guest insights are coming soon."
    />
  );
}

// ============================================================
// REVIEWS TAB — real rating summary + review list when available
// ============================================================

function ReviewsTab({ restaurant, analytics }) {
  const rating = ratingValue(restaurant);
  const count = reviewCount(restaurant);
  const liveReviews = Array.isArray(analytics?.review_inbox) ? analytics.review_inbox : [];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <KpiTile label="Avg rating" value={rating ? `${rating.toFixed(1)}★` : '—'} hint={rating ? 'from diner reviews' : 'no ratings yet'} tone="orange" />
        <KpiTile label="Reviews" value={count !== null ? String(count) : '—'} hint="on ChatWithMenu" tone="blue" />
      </div>

      {liveReviews.length === 0 ? (
        <EmptyCard
          tone="orange"
          eyebrow="REVIEW INBOX"
          title="Read and reply to diner reviews"
          body={count ? 'The review inbox and replies are coming soon.' : 'No reviews yet.'}
        />
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {liveReviews.map((r) => <ReviewCard key={r.id} review={r} />)}
        </div>
      )}
    </div>
  );
}

function ReviewCard({ review }) {
  const name = review.user_name || 'Diner';
  const initials = name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const daysAgo = review.datetime
    ? Math.max(0, Math.floor((Date.now() - new Date(review.datetime).getTime()) / 86400000))
    : null;
  const tags = review.allergen_tags || [];
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.26, ease: EASE }}
      className="cwm-sheet" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 12,
          background: 'linear-gradient(160deg, #1E9BFF, #0F7FE8)',
          color: '#fff', display: 'grid', placeItems: 'center',
          fontSize: 12, fontWeight: 900, flexShrink: 0,
        }}>
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)' }}>{name}</div>
            <StarTiny value={review.rating} />
          </div>
          {daysAgo !== null && (
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>
              {daysAgo === 0 ? 'today' : daysAgo === 1 ? 'yesterday' : `${daysAgo}d ago`}
            </div>
          )}
        </div>
      </div>
      {review.review_text && (
        <p style={{ margin: '12px 0 0', fontSize: 13.5, color: 'var(--cwm-gray-700)', fontWeight: 500, lineHeight: 1.55 }}>
          "{review.review_text}"
        </p>
      )}
      {tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 10 }}>
          {tags.map((t) => (
            <span key={t} style={{
              padding: '2px 9px', borderRadius: 999,
              background: 'var(--cwm-red-light)', color: 'var(--cwm-red-text)',
              fontSize: 10, fontWeight: 800,
            }}>⚠ {t}</span>
          ))}
        </div>
      )}
    </motion.div>
  );
}

function StarTiny({ value = 0, size = 10 }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill={i <= value ? '#F6B93B' : 'none'} stroke={i <= value ? '#F6B93B' : 'var(--cwm-gray-300)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 18l-6.2 3 1.2-6.8-5-4.9 6.9-1z"/>
        </svg>
      ))}
    </span>
  );
}

// ============================================================
// STAFF TAB
// ============================================================

function StaffTab({ onTrain }) {
  return (
    <EmptyCard
      tone="green"
      eyebrow="STAFF"
      title="Your team"
      body="Staff roster, invites and shift scheduling are coming soon."
      action={(
        <button type="button" onClick={onTrain}
                style={{
                  padding: '9px 14px', borderRadius: 999,
                  background: '#fff', color: 'var(--cwm-blue-deep)',
                  border: '1.5px solid var(--cwm-blue-light)',
                  fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
          <QuizIcon size={12} /> Training quiz
        </button>
      )}
    />
  );
}

// ============================================================
// ICONS
// ============================================================

function TabIcon({ name, size = 14 }) {
  const paths = {
    chart:     'M3 3v18h18M7 15l4-4 4 4 5-7',
    menu:      'M4 4h10v6H4zM4 13h10v6H4zM17 4h3v16h-3z',
    flame:     'M12 3s4 4 4 8a4 4 0 01-8 0c0-2 1-3 2-4 0 1 0 2 1 2 0-2 1-4 1-6z',
    shield:    'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
    people:    'M9 11a3 3 0 100-6 3 3 0 000 6zM17 12a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM3 19c0-3 3-5 6-5s6 2 6 5M16 19c0-2 1.5-4 4-4',
    store:     'M3 10l1-6h16l1 6M3 10v10h18V10M3 10h18',
    badge:     'M12 2l3 6 6 1-5 4 1 7-5-3-5 3 1-7-5-4 6-1z',
    star:      'M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 18l-6.2 3 1.2-6.8-5-4.9 6.9-1z',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill={name === 'flame' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name] || paths.chart}/></svg>;
}

function QuizIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9 9a3 3 0 016 0c0 1.5-1 2-2 2.5S12 13 12 14M12 17h.01"/></svg>; }
function PencilIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function ArrowIcon({ size = 14, color = 'currentColor' }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>; }
